import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Badge, Tooltip } from 'flowbite-react';
import { Change, User } from '../../models';
import { ChangeTrack, EmailAuthBadges, Icon, TriageRowActions } from '../../components';
import type { IconType } from '../../components';
import {
    handleChangeMiddleClick,
    preventMiddleClickAutoscroll,
} from '../../utils/openChangeInNewBrowserTab';
import { classifySpamSource } from '../../utils/spamSource';

const STATUS_SPAM_SUSPECT = 'spam_suspect';
const TYPE_DOMAIN_DEV = 'dev';
const TYPE_DOMAIN_NON_DEV = 'non_dev';
const NON_DEV_SUBTYPES = ['support', 'sales', 'oncall', 'abuse', 'ops', 'other'];
const DEV_SUBTYPES = ['fix', 'feature', 'ui', 'i18n', 'refactor', 'test', 'doc', 'lint', 'build', 'release', 'chore'];

const SUBTYPE_ICON_MAP: Record<string, IconType> = {
    fix: 'bug', feature: 'feature', ui: 'ui', i18n: 'globe', refactor: 'code',
    test: 'test', doc: 'notes', lint: 'lint', build: 'build', release: 'release', chore: 'chore',
    support: 'ticket', sales: 'bill', oncall: 'timer', abuse: 'warning', ops: 'setting', other: 'dots',
};

export type TriageGridCardProps = {
    item: Change;
    isSelected: boolean;
    summary: string;
    language: string;
    user?: User;
    showTrash: boolean;
    typeOverride?: { domain: string; subtype: string };
    canClaim: boolean;
    isQueueItem: boolean;
    onToggleSelect: (changeId: string) => void;
    onOpen: (item: Change) => void;
    onTake: (item: Change) => void;
    onModify: (item: Change) => void;
    onMerge: (item: Change) => void;
    onResolve: (item: Change) => void;
    onDiscard: (item: Change) => void;
    onRestore: (item: Change) => void;
    onMarkSafe: (item: Change) => void;
    onTrustSender: (item: Change) => void;
    onTrustDomain: (item: Change) => void;
    onUpdateType: (item: Change, domain: string, subtype: string) => void;
};

/** One triage card in grid mode — memoized to limit re-renders when selection changes. */
function TriageGridCard(props: TriageGridCardProps) {
    const {
        item, isSelected, summary, language, user, showTrash, typeOverride,
        canClaim, isQueueItem, onToggleSelect, onOpen, onTake, onModify, onMerge,
        onResolve, onDiscard, onRestore, onMarkSafe, onTrustSender, onTrustDomain, onUpdateType,
    } = props;
    const { t } = useTranslation();

    const createdAtFromNow = item.createdAt ? moment(item.createdAt).fromNow() : '-';
    const isSpam = item.progress?.status === STATUS_SPAM_SUSPECT;
    const spamSourceBadge = isSpam ? classifySpamSource(item.intake?.spamSource) : null;
    const senderEmail = item.intake?.sender?.email || '';
    const senderDomain = item.intake?.sender?.domain
        || (senderEmail.includes('@') ? senderEmail.split('@').pop() : '');
    const currentDomain = typeOverride?.domain || item.typeDomain || inferDomainFromType(item.type);
    const currentSubtype = typeOverride?.subtype || inferSubtypeFromChange(item);

    return (
        <div
            className={`rounded-lg border bg-white dark:bg-gray-800 p-3 shadow-sm ${isSpam ? 'border-amber-300' : 'border-gray-100 dark:border-gray-700'}`}
        >
            <div
                className="mb-2 flex cursor-pointer items-center justify-between gap-2"
                onClick={() => onToggleSelect(item._id)}
            >
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleSelect(item._id)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 cursor-pointer"
                    />
                    <Badge color="indigo" size="xs" className="w-fit">
                        <Icon type="priority" className="mr-1 inline" />
                        {item.priority}
                    </Badge>
                    <Badge color={item.color()} size="xs" className="w-fit">
                        {item.slugName()}
                    </Badge>
                    <Badge color="gray" size="xs" className="w-fit">
                        {item.project?.name}
                    </Badge>
                    {spamSourceBadge && (
                        <Badge color={spamSourceBadge.color} size="xs" className="w-fit">
                            {t(spamSourceBadge.labelKey)}
                        </Badge>
                    )}
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {item.isTimeTrackable(user) && (
                        <div className="rounded bg-blue-50 dark:bg-blue-950/40 px-1">
                            <ChangeTrack change={item} className="scale-75" />
                        </div>
                    )}
                    {!showTrash && (
                        <TriageRowActions
                            showTake={canClaim}
                            showModify={!isSpam}
                            showMerge={!isSpam && !!item.technicalContext?.mergeReview?.candidateId}
                            showResolve={isQueueItem && !isSpam}
                            showDiscard={isQueueItem}
                            showMarkSafe={isSpam}
                            showTrustSender={isSpam && !!senderEmail}
                            showTrustDomain={isSpam && !!senderDomain}
                            trustSenderTitle={senderEmail
                                ? `${t('priorities.trust_sender')} — ${senderEmail}`
                                : undefined}
                            trustDomainTitle={senderDomain
                                ? `${t('priorities.trust_domain')} — ${senderDomain}`
                                : undefined}
                            onTake={() => onTake(item)}
                            onModify={() => onModify(item)}
                            onMerge={() => onMerge(item)}
                            onResolve={() => onResolve(item)}
                            onDiscard={() => onDiscard(item)}
                            onMarkSafe={() => onMarkSafe(item)}
                            onTrustSender={() => onTrustSender(item)}
                            onTrustDomain={() => onTrustDomain(item)}
                        />
                    )}
                    {showTrash && (
                        <TriageRowActions
                            showRestore
                            onRestore={() => onRestore(item)}
                        />
                    )}
                </div>
            </div>

            <div
                onClick={() => onOpen(item)}
                onMouseDown={preventMiddleClickAutoscroll}
                onAuxClick={(event) => handleChangeMiddleClick(event, item)}
                className="cursor-pointer"
            >
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {item.localizedTitle(language) || ''}
                </h4>
                <p className="mt-1 line-clamp-2 text-xs text-gray-600 dark:text-gray-300">
                    {summary || t('priorities.no_summary')}
                </p>
                {item.intake?.headers && (
                    <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                        <EmailAuthBadges intake={item.intake} compact />
                    </div>
                )}
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    #{item.slugName()} — {createdAtFromNow}
                </div>
            </div>

            <div
                className="mt-2 flex flex-wrap gap-1 border-t border-gray-100 dark:border-gray-700 pt-2"
                onClick={(e) => e.stopPropagation()}
            >
                {DEV_SUBTYPES.map((subtype) => (
                    <Tooltip key={subtype} content={subtype}>
                        <button
                            className={`rounded p-1 transition-colors ${
                                currentDomain === TYPE_DOMAIN_DEV && currentSubtype === subtype
                                    ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700'
                                    : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-gray-700 dark:text-gray-200'
                            }`}
                            onClick={() => onUpdateType(item, TYPE_DOMAIN_DEV, subtype)}
                        >
                            <Icon type={SUBTYPE_ICON_MAP[subtype]} size={3} />
                        </button>
                    </Tooltip>
                ))}
                <span className="mx-1 self-stretch border-l border-gray-300 dark:border-gray-600" />
                {NON_DEV_SUBTYPES.map((subtype) => (
                    <Tooltip key={subtype} content={subtype}>
                        <button
                            className={`rounded p-1 transition-colors ${
                                currentDomain === TYPE_DOMAIN_NON_DEV && currentSubtype === subtype
                                    ? 'bg-green-100 dark:bg-green-900/40 text-green-700'
                                    : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-gray-700 dark:text-gray-200'
                            }`}
                            onClick={() => onUpdateType(item, TYPE_DOMAIN_NON_DEV, subtype)}
                        >
                            <Icon type={SUBTYPE_ICON_MAP[subtype]} size={3} />
                        </button>
                    </Tooltip>
                ))}
            </div>
        </div>
    );
}

const inferDomainFromType = (type?: string) => {
    if (!type || type === Change.TYPE_NONE)
        return TYPE_DOMAIN_NON_DEV;
    if (DEV_SUBTYPES.includes(type))
        return TYPE_DOMAIN_DEV;
    return TYPE_DOMAIN_NON_DEV;
};

const inferSubtypeFromChange = (item: Change) => item.typeSubtype || item.type || 'other';

export default React.memo(TriageGridCard);
